import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Lightbulb, AlertTriangle, TrendingUp, Target, RefreshCw, 
  Sparkles, CheckCircle2, Eye, Play, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsightCard = ({ insight, onMarkRead, onMarkActed }) => {
  const getTypeConfig = (type) => {
    switch (type) {
      case 'risk':
        return { icon: AlertTriangle, class: 'insight-card-risk', color: 'text-red-600' };
      case 'trend':
        return { icon: TrendingUp, class: 'insight-card-trend', color: 'text-orange-600' };
      case 'pip_suggestion':
        return { icon: Target, class: 'insight-card-pip', color: 'text-teal-600' };
      default:
        return { icon: Lightbulb, class: 'insight-card-recommendation', color: 'text-blue-600' };
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500 text-black">Medium</Badge>;
      default:
        return <Badge variant="secondary">Low</Badge>;
    }
  };

  const config = getTypeConfig(insight.insight_type);
  const TypeIcon = config.icon;

  return (
    <Card 
      className={`${insight.is_read ? 'opacity-75' : ''} transition-all duration-200 hover:shadow-md`}
      data-testid={`insight-card-${insight.id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
            insight.insight_type === 'risk' ? 'bg-red-100 dark:bg-red-950' :
            insight.insight_type === 'trend' ? 'bg-orange-100 dark:bg-orange-950' :
            insight.insight_type === 'pip_suggestion' ? 'bg-teal-100 dark:bg-teal-950' :
            'bg-blue-100 dark:bg-blue-950'
          }`}>
            <TypeIcon className={`h-5 w-5 ${config.color}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {getSeverityBadge(insight.severity)}
              <Badge variant="outline" className="capitalize">{insight.insight_type.replace('_', ' ')}</Badge>
              {insight.is_acted_upon && (
                <Badge className="bg-green-500">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Acted
                </Badge>
              )}
            </div>
            
            <h3 className="font-semibold text-lg mb-2">{insight.title}</h3>
            <p className="text-muted-foreground mb-4">{insight.summary}</p>
            
            {insight.details && (
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <p className="text-sm">{insight.details}</p>
              </div>
            )}
            
            {insight.recommended_actions?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Recommended Actions:</p>
                <ul className="space-y-1">
                  {insight.recommended_actions.map((action, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <ChevronRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {insight.related_metrics?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {insight.related_metrics.map((metric, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{metric}</Badge>
                ))}
              </div>
            )}
            
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                {new Date(insight.created_at).toLocaleString()}
              </p>
              <div className="flex gap-2">
                {!insight.is_read && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onMarkRead(insight.id)}
                    data-testid={`mark-read-${insight.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Mark Read
                  </Button>
                )}
                {!insight.is_acted_upon && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onMarkActed(insight.id)}
                    data-testid={`mark-acted-${insight.id}`}
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Mark Acted
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QualityConfidenceScore = ({ score }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return 'text-green-500';
    if (s >= 60) return 'text-yellow-500';
    if (s >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <Card data-testid="quality-confidence-score">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Quality Confidence Score</p>
            <p className={`text-5xl font-bold ${getScoreColor(score)}`}>{score}</p>
            <p className="text-xs text-muted-foreground mt-1">Based on latest AI analysis</p>
          </div>
          <div className="h-16 w-16 rounded-full border-4 border-muted flex items-center justify-center">
            <Sparkles className={`h-8 w-8 ${getScoreColor(score)}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Insights() {
  const { token } = useAuth();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);

  const fetchInsights = async () => {
    try {
      const response = await fetch(`${API_URL}/api/insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
        if (data.length > 0) {
          setQualityScore(data[0].quality_confidence_score);
        }
      }
    } catch (error) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [token]);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/insights/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('AI insights generated', {
          description: `${data.insights?.length || 0} new insights created`
        });
        setQualityScore(data.quality_confidence_score);
        fetchInsights();
      } else {
        const error = await response.json();
        toast.error('Failed to generate insights', { description: error.detail });
      }
    } catch (error) {
      toast.error('Connection error', { description: 'Unable to generate insights' });
    } finally {
      setGenerating(false);
    }
  };

  const markInsightRead = async (insightId) => {
    try {
      await fetch(`${API_URL}/api/insights/${insightId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInsights();
    } catch (error) {
      toast.error('Failed to update insight');
    }
  };

  const markInsightActed = async (insightId) => {
    try {
      await fetch(`${API_URL}/api/insights/${insightId}/acted`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Insight marked as acted upon');
      fetchInsights();
    } catch (error) {
      toast.error('Failed to update insight');
    }
  };

  const unreadCount = insights.filter(i => !i.is_read).length;
  const criticalCount = insights.filter(i => i.severity === 'critical' && !i.is_acted_upon).length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="insights-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32 lg:col-span-2" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Insights & Intelligence</h1>
          <p className="text-muted-foreground mt-1">AI-powered analysis of your quality metrics</p>
        </div>
        <Button 
          onClick={generateInsights} 
          disabled={generating}
          data-testid="generate-insights-btn"
        >
          {generating ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate New Insights
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <QualityConfidenceScore score={qualityScore || 70} />
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Unread Insights</p>
                <p className="text-4xl font-bold">{unreadCount}</p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={criticalCount > 0 ? 'border-red-500' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Critical Actions</p>
                <p className={`text-4xl font-bold ${criticalCount > 0 ? 'text-red-500' : ''}`}>
                  {criticalCount}
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${criticalCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights List */}
      {insights.length === 0 ? (
        <Card className="py-12">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Lightbulb className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No Insights Yet</h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              Enter quality metrics data and generate AI-powered insights to identify risks, 
              trends, and improvement opportunities.
            </p>
            <Button onClick={generateInsights} disabled={generating}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate First Insights
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div 
              key={insight.id} 
              className="animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <InsightCard
                insight={insight}
                onMarkRead={markInsightRead}
                onMarkActed={markInsightActed}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
